export const SERVICES = [
    {
        id: 'backup',
        name: 'Backup Manager',
        icon: 'shield',
        href: '/dashboard',
        match: ['/dashboard', '/databases', '/backups', '/jobs', '/audit'],
    },
    {
        id: 'auth',
        name: 'Auth Manager',
        icon: 'lock',
        href: '/auth',
        match: ['/auth'],
    },
    {
        id: 'tailscale',
        name: 'Tailscale Manager',
        icon: 'tailscale',
        href: '/tailscale',
        match: ['/tailscale'],
    },
    {
        id: 'opengater',
        name: 'Opengater Manager',
        // Self-hosted copy lives in frontend/public/opengater-logo.png — needed
        // because our host nginx CSP restricts `img-src` to 'self' + data: and
        // would block a direct opengater.com fetch.
        iconUrl: '/opengater-logo.png',
        href: '/opengater',
        match: ['/opengater'],
    },
];
export const STICKY_SERVICE_KEY = 'sidebar_last_service';
function fallbackService() {
    return SERVICES.find((s) => s.id === 'backup');
}
/**
 * Pick the active service based on current URL.
 *
 * Three-tier resolution:
 *   1. If the path matches one of a service's explicit prefixes (e.g.
 *      `/dashboard` → backup, `/auth/services` → auth), that wins.
 *   2. Otherwise — typically shared routes like `/settings` or
 *      `/notifications` — fall back to the LAST service the user was
 *      on (persisted in localStorage). This is the "sticky service"
 *      behaviour: clicking Settings from Auth Manager keeps the sidebar
 *      on Auth Manager nav.
 *   3. If nothing remembered, default to backup (the original service).
 *
 * `lastKnownId` is read from `STICKY_SERVICE_KEY` at sidebar mount and
 * kept in state to avoid storage reads on every render.
 */
export function detectCurrentService(pathname, lastKnownId) {
    // Tier 1: explicit prefix match (longest wins to keep `/auth/x` from
    // accidentally matching a hypothetical `/au` prefix).
    let best = null;
    let bestLen = -1;
    for (const svc of SERVICES) {
        if (!svc.match)
            continue;
        for (const prefix of svc.match) {
            if (pathname === prefix || pathname.startsWith(prefix + '/')) {
                if (prefix.length > bestLen) {
                    best = svc;
                    bestLen = prefix.length;
                }
            }
        }
    }
    if (best)
        return best;
    // Tier 2: sticky last-known.
    if (lastKnownId) {
        const last = SERVICES.find((s) => s.id === lastKnownId);
        if (last)
            return last;
    }
    // Tier 3: default.
    return fallbackService();
}
/**
 * Did this URL definitively match one of the services' explicit prefixes?
 * Used to decide whether we should update the sticky-service localStorage
 * — only update on definitive matches, never on shared paths like
 * `/settings`, otherwise we'd erase context the moment the user navigates
 * to a shared page.
 */
export function pathIsServiceSpecific(pathname) {
    for (const svc of SERVICES) {
        if (!svc.match)
            continue;
        for (const prefix of svc.match) {
            if (pathname === prefix || pathname.startsWith(prefix + '/'))
                return true;
        }
    }
    return false;
}
/** The last service the user was on (see `detectCurrentService`), if storage allows. */
export function readStickyService() {
    try {
        return localStorage.getItem(STICKY_SERVICE_KEY);
    }
    catch {
        return null;
    }
}
